import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import styled from "styled-components";
import { Anchor as S } from "../../node_modules/@zillow/constellation-10/dist/components/anchor/anchor.esm.js";
import { DataSelect as S$1 } from "../../node_modules/@zillow/constellation-10/dist/components/data-select/data-select.esm.js";
import { List as r } from "../../node_modules/@zillow/constellation-10/dist/components/list/list.esm.js";
import { Pagination as t } from "../../node_modules/@zillow/constellation-10/dist/components/pagination/pagination.esm.js";
import { Paragraph as d } from "../../node_modules/@zillow/constellation-10/dist/components/paragraph/paragraph.esm.js";
import { Table as H } from "../../node_modules/@zillow/constellation-10/dist/components/table/table.esm.js";
import { dollar } from "../../node_modules/@zillow/formatters/dist/formatters.esm.esm.js";
import { last } from "lodash";
import { useState, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { handleFieldClickEvent, handleFieldChangeEvent } from "./AmortizationCalculator.Clickstream.esm.js";
import { useLastValidAmortizationValues, getZillowURL } from "./AmortizationCalculator.utils.esm.js";
import { FORM_FIELD } from "./constants.esm.js";
const AMORTIZATION_BREAKDOWN_ID = "amortization-breakdown";
const TABLE_DISPLAY_MODE = {
  MONTH: "month",
  YEAR: "year"
};
const OPTIONS = {
  "Show by month": TABLE_DISPLAY_MODE.MONTH,
  "Show by year": TABLE_DISPLAY_MODE.YEAR
};
const MODES = {
  DATE_BASED: "date"
};
const getPageSizeForShowByValue = (showBy) => showBy === TABLE_DISPLAY_MODE.YEAR ? 10 : 12;
const AmortizationScheduleTable = ({
  mode = MODES.DATE_BASED
}) => {
  const form = useFormContext();
  const amortization = useLastValidAmortizationValues(form).calculations.amortization;
  const [showBy, setShowBy] = useState(TABLE_DISPLAY_MODE.MONTH);
  const [pageSize, setPageSize] = useState(getPageSizeForShowByValue(showBy));
  const [currentPage, setCurrentPage] = useState(0);
  useEffect(() => {
    setCurrentPage(0);
    setPageSize(getPageSizeForShowByValue(showBy));
  }, [showBy]);
  const filteredAmortization = useMemo(() => showBy === TABLE_DISPLAY_MODE.YEAR ? amortization.filter((x) => x.month % 12 - 1 === 0) : amortization, [showBy, amortization]);
  const handleDataSelectChange = (e) => {
    const value = e.target.value;
    if (value === "year") {
      handleFieldChangeEvent("Show by year", FORM_FIELD);
    } else if (value === "month") {
      handleFieldChangeEvent("Show by month", FORM_FIELD);
    }
    setShowBy(value);
  };
  const amortizationPage = useMemo(() => filteredAmortization.slice(currentPage * pageSize, (currentPage + 1) * pageSize), [filteredAmortization, pageSize, currentPage]);
  const lastAmortization = last(amortization);
  const totalPrincipal = (lastAmortization == null ? void 0 : lastAmortization.totalPrincipal) ?? 0;
  const totalInterest = (lastAmortization == null ? void 0 : lastAmortization.totalInterest) ?? 0;
  const breakdownContent = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(_StyledParagraph, { children: [
      "Our mortgage amortization schedule makes it easy to see how much of your mortgage payment will go toward paying interest and principal over your loan term. You can view amortization by month or year. Keep in mind, your monthly mortgage payment may also include",
      " ",
      /* @__PURE__ */ jsxs(S, { href: getZillowURL("mortgage-learning/what-is-piti/"), children: [
        "property taxes and home insurance",
        " "
      ] }),
      " ",
      "- which aren't included in this amortization schedule, since the payments may fluctuate throughout your loan term."
    ] }),
    /* @__PURE__ */ jsxs(_StyledListRoot, { "aria-live": "polite", "aria-atomic": "true", children: [
      /* @__PURE__ */ jsxs(r.Item, { children: [
        "Total principal payments: ",
        dollar(totalPrincipal)
      ] }),
      /* @__PURE__ */ jsxs(r.Item, { children: [
        "Total interest payments: ",
        dollar(totalInterest)
      ] })
    ] }),
    /* @__PURE__ */ jsx(_StyledDataSelect, { "aria-label": "Amortization schedule breakdown show by time length dropdown", defaultValue: "month", data: OPTIONS, onChange: handleDataSelectChange }),
    /* @__PURE__ */ jsxs(H.Root, { appearance: "zebra", "aria-label": "Amortization schedule breakdown table", "aria-live": "polite", "aria-atomic": "true", children: [
      /* @__PURE__ */ jsx(H.Header, { children: /* @__PURE__ */ jsx(H.Row, { children: mode === MODES.DATE_BASED ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(H.HeaderCell, { children: "Date" }),
        /* @__PURE__ */ jsx(H.HeaderCell, { children: "Interest" }),
        /* @__PURE__ */ jsx(H.HeaderCell, { children: "Principal" }),
        /* @__PURE__ */ jsx(H.HeaderCell, { children: "Principal remaining" })
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(H.HeaderCell, { children: "Month" }),
        /* @__PURE__ */ jsx(H.HeaderCell, { children: "Interest" }),
        /* @__PURE__ */ jsx(H.HeaderCell, { children: "Principal" }),
        /* @__PURE__ */ jsx(H.HeaderCell, { children: "Principal remaining" })
      ] }) }) }),
      /* @__PURE__ */ jsx(H.Body, { children: amortizationPage.map((item, index) => /* @__PURE__ */ jsxs(H.Row, { children: [
        /* @__PURE__ */ jsx(H.Cell, { children: mode === MODES.DATE_BASED ? item.formattedDate : item.month }),
        /* @__PURE__ */ jsx(H.Cell, { children: dollar(item.monthlyInterest) }),
        /* @__PURE__ */ jsx(H.Cell, { children: dollar(item.monthlyPrincipal) }),
        /* @__PURE__ */ jsx(H.Cell, { children: dollar(item.remainingBalance) })
      ] }, `${item.remainingBalance}-${index}`)) })
    ] }),
    /* @__PURE__ */ jsx(_StyledPagination, { "aria-label": `Amortization breakdown pagination navigation by ${showBy}`, currentPageIndex: currentPage, onPageSelected: setCurrentPage, totalPages: Math.ceil(filteredAmortization.length / pageSize), onClick: () => handleFieldClickEvent("Prev/Next", "button") })
  ] });
  return /* @__PURE__ */ jsx("div", { id: AMORTIZATION_BREAKDOWN_ID, children: breakdownContent });
};
var _StyledParagraph = styled(d)({
  marginTop: "tighter",
  marginBottom: "tighter"
});
var _StyledListRoot = styled(r.Root)({
  marginTop: "tighter",
  marginBottom: "tighter"
});
var _StyledDataSelect = styled(S$1)({
  marginBottom: "default"
});
var _StyledPagination = styled(t).withConfig({
  displayName: "AmortizationScheduleTable___StyledPagination",
  componentId: "sc-1a9xb70-0"
})({
  marginTop: "default"
});
export {
  AMORTIZATION_BREAKDOWN_ID,
  MODES,
  AmortizationScheduleTable as default
};
